import { Tooltip, TooltipContainer } from '@tkww/orion-web-ds';
import { Icon } from '@tkww/orion-web-ds-graphic';
import React, {
	useState,
	useRef,
	useLayoutEffect,
	useCallback,
	type FC,
} from 'react';
import Styles from './styles.scss';

export const TooltipInfo: FC = () => {
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const [arrowOffset, setArrowOffset] = useState<number>(50);

	const tooltipTargetRef = useRef<HTMLButtonElement | null>(null);
	const tooltipRef = useRef<HTMLDivElement | null>(null);

	const computeArrowOffset = useCallback(() => {
		if (!tooltipTargetRef.current || !tooltipRef.current) return;

		const targetRect = tooltipTargetRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current.getBoundingClientRect();
		if (!tooltipRect.width) return;

		const tooltipStyles = window.getComputedStyle(tooltipRef.current);
		const padLeft = Number.parseFloat(tooltipStyles.paddingLeft || '0');
		const padRight = Number.parseFloat(tooltipStyles.paddingRight || '0');
		const contentWidth = tooltipRect.width - padLeft - padRight;
		if (contentWidth <= 0) return;

		const centerX = targetRect.left + targetRect.width / 2;
		const relativeX = centerX - (tooltipRect.left + padLeft);
		let percent = (relativeX / contentWidth) * 100;
		percent = Math.max(0, Math.min(100, percent));
		setArrowOffset(Math.round(percent));
	}, []);

	useLayoutEffect(() => {
		if (!isTooltipOpen) return;
		computeArrowOffset();
		const rafId = requestAnimationFrame(() => computeArrowOffset());
		const handleResize = () => computeArrowOffset();
		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleResize);
		return () => {
			cancelAnimationFrame(rafId);
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleResize);
		};
	}, [computeArrowOffset, isTooltipOpen]);

	const touchHandledRef = useRef(false);
	const toggleOpenTooltip = (
		e: React.MouseEvent | React.KeyboardEvent | React.TouchEvent,
	) => {
		e.stopPropagation();
		if (e.type === 'touchstart') {
			touchHandledRef.current = true;
		} else if (e.type === 'click' && touchHandledRef.current) {
			touchHandledRef.current = false;
			return;
		}
		setIsTooltipOpen((prev) => !prev);
	};

	const handleMouseEnter = (e: React.MouseEvent) => {
		setIsTooltipOpen(true);
	};

	const handleMouseLeave = (e: React.MouseEvent) => {
		setIsTooltipOpen(false);
	};

	return (
		<>
			<button
				type="button"
				className={Styles.tooltipTarget}
				onClick={toggleOpenTooltip}
				onTouchStart={toggleOpenTooltip}
				onKeyDown={toggleOpenTooltip}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				data-testid="vendor-network-tooltip"
				ref={tooltipTargetRef}
			>
				<Icon
					size="sm"
					name="info"
					color="info"
					className={Styles.icon}
					aria-label="More information"
				/>
			</button>
			<div className={Styles.tooltipWrapper}>
				<TooltipContainer
					data-testid="tooltip-container"
					className={Styles.tooltipContainer}
				>
					{isTooltipOpen && (
						<Tooltip
							size="md"
							arrowPosition="top"
							arrowOffset={arrowOffset}
							className={Styles.tooltip}
							ref={tooltipRef}
						>
							Couples with your venue also booked these pros who participate in
							paid advertising.
						</Tooltip>
					)}
				</TooltipContainer>
			</div>
		</>
	);
};
